/**
 * @ComponentFor MagazineBlockViewModel
 */
import connect from 'Shared/connect';
import { isCompact as functionIsCompact } from 'Shared/Viewport';
import React from 'react';
import { styled, StyledProps } from '@glitz/react';
import MagazineBlockViewModel from './MagazineBlockViewModel.type';
import State from 'Shared/State';
import { LinkButton } from 'Shared/TextButtons';
import ArticleListItemViewModel from 'Shared/ViewModels/ContentListItemViewModel.type';
import SwiperList from 'Shared/SwiperList';
import { RatioPicture, Preset, Ratio } from 'Shared/Image/Ratio';
import Link from 'Shared/Link';
import { H2, H3, Paragraph, ArticleTag, ResponsiveSize, TextLabel } from 'Shared/SharedComponents/atoms/Typography';
import { GridWrap } from 'Shared/PageLayout';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import * as style from 'Shared/Style';

const Wrap = styled.div({
  backgroundColor: style.colors.brandColors.lightBlue,
  color: style.colors.monochrome.white,
  padding: { y: '80px' },
  ['@media ' + style.mediaMinLarge]: {
    padding: { y: '120px' },
  },
})

const LabelWrap = styled.div({
  marginBottom: '24px',
  alignSelf: 'end',
  justifySelf: 'center'
})

const TextWrap = styled.div({
  marginTop: '24px',
  alignSelf: 'start'
})

const Heading = styled(H2, {
  textAlign: 'center'
})

const Text = styled(Paragraph, {
  textAlign: 'center'
})

const ButtonWrap = styled.div({
  marginTop: '24px',
  justifySelf: 'center'
})

const SliderWrap = styled.div({
  marginTop: '48px',
})

const ImageWrap = styled.div({
  marginBottom: '16px',
})

const ContentWrap = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '12px',
})

const ArticleCard = styled((props: { article: ArticleListItemViewModel; isCompact: boolean } & StyledProps) => {
  const article = props.article as ArticleListItemViewModel;

  return (
    <styled.Div className="white-links">
      <Link to={article.url}>
        <ImageWrap>
          <RatioPicture
            src={article.image?.url}
            alt={''}
            ratio={Ratio.FourToThree}
            preset={Preset.MagazineListItem}
            borderRadius={8}
          />
        </ImageWrap>
        <ContentWrap>
          <ArticleTag>{article.tag}</ArticleTag>
          <H3 upperCase={false}>{article.heading}</H3>
          <ArticleTag>
            {article.tags?.slice(0, 3).join(', ')}
            {(article?.tags?.length ?? 0) > 3 && `, +${(article?.tags?.length as number) - 3}`}
          </ArticleTag>
        </ContentWrap>
      </Link>
    </styled.Div>
  );
});

export const MagazineBlock = connect(
  (state) => ({
    currentBreakpoint: state.currentBreakpoint,
  }),
  (dispatch) => ({}),
)((props: MagazineBlockViewModel & { currentBreakpoint: State['currentBreakpoint'] }) => {
  const { currentBreakpoint } = props;
  const isCompact = functionIsCompact(currentBreakpoint);

  const slidesPerView = isCompact ? 1.25 : 2.25;
  const spaceBetween = isCompact ? 12 : 16;
  const showNav = (slidesPerView < props.items?.length) && !isCompact;

  return (
    <Wrap>
      <GridWrap className="grid">
        <LabelWrap className={createGridClasses(12, 4, createOffsetGridClassLarge(5))}>
          <TextLabel>{props.label}</TextLabel>
        </LabelWrap>
        <div className={createGridClasses(12, 8, createOffsetGridClassLarge(3))}>
          <Heading fontSize={ResponsiveSize.D96_M40}>
            {props.heading}
          </Heading>
        </div>
        <TextWrap className={createGridClasses(12, 4, createOffsetGridClassLarge(5))}>
          <Text>{props.mainIntro}</Text>
        </TextWrap>

        {props.button?.text && props.button?.url && (
          <ButtonWrap className={createGridClasses(12, 12)}>
            <LinkButton to={props.button.url} text={props.button.text} title={props.button.title} />
          </ButtonWrap>
        )}
        <SliderWrap className={createGridClasses(12, 11, createOffsetGridClassLarge(2))}>
          <div>
            <SwiperList
              slidesPerView={slidesPerView}
              slidesPerGroup={isCompact ? 1 : 2}
              spaceBetween={spaceBetween}
              showNav={showNav}
              alternativeNav={true}
              additionalMargin="16px"
              isCompact={isCompact}
              cssMode={true}
              showSwirl={isCompact}
              navColor={style.colors.monochrome.white}
            >
              {props.items?.map((article, index) => (
                <div key={index} className="swiper-slide">
                  <ArticleCard isCompact={isCompact} article={article as ArticleListItemViewModel} />
                </div>
              ))}
            </SwiperList>
          </div>
        </SliderWrap>
      </GridWrap>
    </Wrap>
  );
});

export default MagazineBlock;
